import type { FC } from 'react';
import React from 'react';
import { FormDesktop } from '@alfa-bank/site-form/desktop';
import { FormBirthday as Birthday } from '@alfa-bank/site-form-birthday';
import { OneFieldFioDesktop as OneFieldFio } from '@alfa-bank/site-form-one-field-fio/desktop';
import { TextDesktop as Text } from '@alfa-bank/site-text-v2/desktop';
import { Aligner } from '@alfa-bank/site-aligner';
import { SecurityWarrantyBlockDesktop as SecurityWarrantyBlock } from '@alfa-bank/site-security-warranty-block/desktop';
import { FormDraftFirstStep } from '@alfa-bank/site-form-draft-first-step';
import { FormProgressBarV2Desktop } from '@alfa-bank/site-form-progress-bar-v2/desktop';

import { InvestPhoneInput } from '../../components/phone';
import { AnalyticsSpy } from '../../components/analytics-spy';
import { BIRTHDAY } from '../../config';
import { DraftSpy } from '../../components/draft-spy';
import { validateTeenage } from '../../helpers/validate-age';

import { TeenagerPlateDesktop as TeenagerPlate } from './components/teenager-plate/desktop';
import type { IProps } from './models';
import { defaultInitStepSettings, defaultInitStepTitleDesktop } from './config/default-values';
import { analyticsInitSmev } from './analytics';
import { useStepInit } from './hooks/use-step-init';
import { TeenagerSubscription } from './components/teenager-subscription/component';

export const InitStepDesktop: FC<IProps> = ({
    accountType,
    analyticsWebView,
    birthdaySettings = defaultInitStepSettings.birthdaySettings,
    fioSettings = defaultInitStepSettings.fioSettings,
    phoneSettings = defaultInitStepSettings.phoneSettings,
    securityWarrantyBlockSettings = defaultInitStepSettings.securityWarrantyBlockSettings,
    title = defaultInitStepTitleDesktop,
    progressBarSettings = defaultInitStepSettings.progressBarSettings,
    teenagerSettings,
    children,
    hasPseudoDraft = false,
}) => {
    const { handleSubmitForm, isLoading, setIsOneFieldFio, showProgressBar } = useStepInit({
        accountType,
        analyticsWebView,
    });

    return (
        <FormDesktop
            analytics={{
                submit: analyticsInitSmev.formSubmitSuccess,
                submitOnFail: analyticsInitSmev.formSubmitFail,
            }}
            encType="application/x-www-form-urlencoded"
            onSubmit={handleSubmitForm}
            view="without-shadow"
        >
            {hasPseudoDraft && (
                <FormDraftFirstStep
                    analytics={analyticsInitSmev.draft}
                    mapFields={{ gender: 'sex', birthday: 'birthDate' }}
                    sentryCaptureException={{ packageName: 'invest-form-smev', teamName: 'invest' }}
                    setIsOneFieldFio={setIsOneFieldFio}
                />
            )}
            <AnalyticsSpy analytics={analyticsInitSmev} />
            <DraftSpy />
            <Aligner styler={{ marginBottom: 24 }}>
                <Text styler={{ marginTop: 0, marginBottom: 0 }} tag="h2" view="medium">
                    {title}
                </Text>
            </Aligner>
            {showProgressBar && (
                <FormProgressBarV2Desktop
                    caption={progressBarSettings.caption}
                    currentStepName={progressBarSettings.currentStepName}
                    hatchValue={progressBarSettings.hatchValue}
                    header={progressBarSettings.header}
                    line={progressBarSettings.line}
                    maxStep={progressBarSettings.maxStep}
                    nextStepName={progressBarSettings.nextStepName}
                    steps={progressBarSettings.steps}
                    styler={{ marginBottom: 24 }}
                    value={progressBarSettings.value}
                />
            )}
            <OneFieldFio
                {...fioSettings}
                analytics={{ expandToThreeFields: analyticsInitSmev.fullNameExpandToThreeFields }}
                dataTestId="fullName"
            />
            <InvestPhoneInput {...phoneSettings} styler={{ marginBottom: 24 }} />
            <Birthday
                {...birthdaySettings}
                customValidator={validateTeenage}
                dataTestId="birthday"
                name={BIRTHDAY}
            />
            <TeenagerSubscription renderOn="teenager">
                <TeenagerPlate
                    {...teenagerSettings}
                    analytics={{
                        view: analyticsInitSmev.teenagerPlateView,
                        buttonClick: analyticsInitSmev.teenagerPlateButtonClick,
                    }}
                />
            </TeenagerSubscription>
            <TeenagerSubscription renderOn="notTeenager">
                <SecurityWarrantyBlock
                    {...securityWarrantyBlockSettings}
                    buttonLoading={isLoading}
                    styler={{ marginTop: 24 }}
                />
                <Text
                    kind="secondary"
                    styler={{ marginBottom: 0, marginTop: 24 }}
                    tag="p"
                    view="primary-small"
                >
                    {children}
                </Text>
            </TeenagerSubscription>
        </FormDesktop>
    );
};
